'use client'

import clsx from 'clsx'
import { find, map } from 'lodash'
import React, { Fragment, useMemo } from 'react'
import { useRouter } from '@/lib/next-shim'
import { useTranslations } from '@/lib/next-shim'
import { Bell } from 'react-feather'
import { Popover, Transition } from '@headlessui/react'
import IconButton from '@mui/material/IconButton'

import Events from '@/components/ui/events/Events'
import { useGetMyInfosQuery } from '@/core/graphql/hooks'
import { EventObjectType } from '@/core/graphql/types'

import { useMarkAsReadMutation } from '../api/markEventAsRead.mutation'

const Notification: React.FC = () => {
  const t = useTranslations()
  const { markEventAsRead } = useMarkAsReadMutation()
  const navigate = useRouter()
  const { data: getMyInfosData, refetch } = useGetMyInfosQuery()

  const myInfos = getMyInfosData?.myInfos
  const userId = myInfos?._id || ''
  const events = useMemo(
    () =>
      map(myInfos?.events, (event) => ({
        id: event._id,
        createdAt: event.createdAt,
        recipients: event.recipients.map((recipient) => ({ userId: recipient.userId, read: recipient.read })),
        type: {
          // @ts-ignore - event type is guaranteed to match translation key
          label: t(`account.header.Notification.type.${event.type}`),
          value: event.type,
        },
        userId,
      })),
    [myInfos, userId, t]
  )

  const redirectOnClick = (eventId: string) => {
    const event = find(myInfos?.events, (event) => event._id === eventId)
    // add redirection
    if (event?.object_type === EventObjectType.Order) {
      // redirect to order details
      // find the reference id from the context
      myInfos?.orders?.find((order) => order._id === event.object_id)?.referenceId &&
        navigate.push(`/account/orders/${myInfos?.orders?.find((order) => order._id === event.object_id)?.referenceId}`)
    } else if (event?.object_type === EventObjectType.Consultation) {
      // redirect to consultation details
      navigate.push(`/account/consultations/${event.object_id}`)
    }
  }

  const getUnreadNotificationsCount = () => {
    // Assuming events are already filtered to only those relevant to the user
    return events.filter((event) => !event.recipients.some((recipient) => recipient.userId === userId && recipient.read)).length
  }

  const unreadCount = getUnreadNotificationsCount()

  // events are mark as read as soon as you click on the bell icon
  const markAllAsRead = async () => {
    for (let i = 0; i < events.length; i++) {
      if (!events[i].recipients.some((recipient) => recipient.userId === userId && recipient.read)) {
        await markEventAsRead({
          variables: {
            payload: {
              eventId: events[i].id,
              recipientId: userId,
            },
          },
        })
      }
    }
    await refetch()
  }

  return (
    <Popover className="relative">
      {({ open }) => (
        <>
          <Popover.Button as={IconButton} aria-label={t('account.header.Notification.title')} onClick={() => markAllAsRead()} className="relative">
            <Bell size={22} aria-hidden="true" className={clsx('transition-colors', open ? 'text-primary' : 'text-gray-600')} />
            {unreadCount > 0 && (
              <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-red-500 px-1 text-[0.625rem] font-bold leading-none text-white ring-2 ring-white">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </Popover.Button>
          <Transition
            show={open}
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <Popover.Panel
              static
              className="absolute right-0 z-20 mt-3 w-[calc(100vw-2rem)] max-w-sm origin-top-right overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5"
            >
              <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-5 py-4">
                <h3 className="text-base font-semibold text-gray-900">{t('account.header.Notification.title')}</h3>
                {unreadCount > 0 && (
                  <span className="inline-flex items-center justify-center rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </div>
              <Events events={events} userId={userId} onNotificationClick={redirectOnClick} emptyLabel={t('account.header.Notification.empty')} />
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  )
}

export default Notification
